/* global Sys, Services, Resources */
Sys.ns("Services");

/**
 * The Paytable Service.
 *
 * @class Services.Paytable
 * @extends Services.Base
 */
Services.Paytable = {
    constructor: function() {
        Services.Paytable.superclass.constructor.apply(this, arguments);

        this._enabled = true;
        this._open = false;
        this._content = [];
        this._currentPage = null;
        this._paytableOnStartupState = "disabled";
        this._paytableValues = {};
        this._paytableClosedFirstTime = false;

        if (Resources.readData("showPaytableOnStartup")) {
            this.fetchPaytableValues();
            this.setPaytableOnStartupState("idle");
        }

        if (this.shouldShowPaytableOnStartup()) {
            this.showPaytableOnEnteringGame();
        }
    },

    _setupEvents: function() {
        this.observable.on({
            "notify:stateHandler.enteringSpinningState": this.close.bind(this),
            "notify:gameModeChanged": this.close.bind(this)
        });
    },

    /**
     * Enables the paytable (if not already enabled).
     *
     * @returns {void}
     */
    enable: function() {
        if (this._enabled) {
            return;
        }

        this._enabled = true;

        this.publish("enabled");
    },

    /**
     * Disables the paytable (if not already disabled).
     *
     * @returns {void}
     */
    disable: function() {
        if (!this._enabled) {
            return;
        }

        this._enabled = false;

        this.publish("disabled");
    },

    /**
     * Opens the paytable (if not already open, or the page number is different).
     *
     * @param {number} [page] The number of the page to open.
     * @returns {void}
     */
    open: function(page) {
        var currentPage = this.getCurrentPage(),
            pageNumber = this._isValidPageNumber(page) ? page : 1;

        this._currentPage = pageNumber;

        if (this._open && pageNumber === currentPage) {
            return;
        }

        this._open = true;

        this.publish("opened", pageNumber);
    },

    /**
     * Closes the paytable (if not already closed).
     *
     * @returns {void}
     */
    close: function() {
        if (Resources.readData("showPaytableOnStartup")) {
            this.setPaytableOnStartupState("idle");
        }

        if (!this._open) {
            return;
        }

        this._open = false;
        this._currentPage = null;

        if (!this._paytableClosedFirstTime) {
            this.observable.fireEvent("notify:paytableClosedFirstTime");
            this._paytableClosedFirstTime = true;
        }

        this.publish("closed");
    },

    /**
     * Sets the content of the paytable.
     *
     * @param {string|string[]} content The content. A string for each page in the paytable.
     * @returns {void}
     */
    setContent: function(content) {
        var pages = content;

        if (typeof content === "string") {
            pages = [content];
        }

        this._content = pages;

        this.publish("contentChanged", this._content);
    },

    /**
     * Moves the paytable to the next page (if showing).
     * Rolls to the first page if the last page is currently being shown.
     *
     * @returns {void}
     */
    nextPage: function() {
        var startingPage = this._currentPage,
            numberOfPages = this.getNumberOfPages();

        if (this.isOpen() && numberOfPages > 1) {
            this._currentPage = this._currentPage + 1;

            if (this._currentPage > numberOfPages) {
                this._currentPage = 1;
            }

            this.publish("pageChanged", this._currentPage, startingPage, true);
        }
    },

    /**
     * Moves the paytable to the previous page (if showing).
     * Rolls to the last page if the first page is currently being shown.
     *
     * @returns {void}
     */
    previousPage: function() {
        var startingPage = this._currentPage,
            numberOfPages = this.getNumberOfPages();

        if (this.isOpen() && numberOfPages > 1) {
            this._currentPage = this._currentPage - 1;

            if (this._currentPage <= 0) {
                this._currentPage = numberOfPages;
            }

            this.publish("pageChanged", this._currentPage, startingPage, true);
        }
    },

    /**
     * Updates the page number when page is changed by bullet navigation.
     *
     * @param {number} [currnePage] The number of the current page.
     * @param {number} [previousPage] The number of the previous page.
     * @returns {void}
     */
    updatePageNumber: function(currnePage, previousPage) {
        this._currentPage = currnePage;
        this.publish("pageChanged", this._currentPage, previousPage, false);
    },

    /**
     * Gets the number of pages in the paytable.
     *
     * @returns {number} The number of pages.
     */
    getNumberOfPages: function() {
        return this._content.length;
    },

    /**
     * Gets the current page index.
     *
     * @returns {number} The current page index.
     */
    getCurrentPage: function() {
        return this._currentPage;
    },

    /**
     * Gets the paytable content.
     *
     * @returns {string[]} The paytable content separated as pages.
     */
    getContent: function() {
        return this._content;
    },

    /**
     * Checks if the paytable is enabled.
     *
     * @returns {boolean} Whether or not the paytable is enabled.
     */
    isEnabled: function() {
        return this._enabled;
    },

    /**
     * Checks if the paytable is currently being shown.
     *
     * @returns {boolean} Whether or not the paytable is shown.
     */
    isOpen: function() {
        return this._open;
    },

    /**
     * Checks if the page number is valid (i.e. A number and in range).
     *
     * @private
     * @param {number} page The page number to validate.
     * @returns {boolean} Whether or not the page is a valid page number.
     */
    _isValidPageNumber: function(page) {
        return (
            typeof page === "number" &&
            !isNaN(page) &&
            page > 0 &&
            page <= this.getNumberOfPages()
        );
    },

    /**
     * Gets Paytable value.
     *
     * @returns {Object} Paytable value.
     */
    getPaytableValues: function() {
        return this._paytableValues;
    },

    /**
     * Informs service that initial setup of the paytable is finished.
     *
     * @returns {void}
     */
    setupFinished: function() {
        this.setPaytableOnStartupState("setupFinished");

        if (this.shouldShowPaytableOnStartup()) {
            this.openPaytableOnStartup();
        }
    },

    /**
     * Checks if the paytable should be shown on startup.
     *
     * @returns {boolean} Whether the paytable should be shown on startup or not.
     */
    shouldShowPaytableOnStartup: function() {
        return this._paytableOnStartupState !== "disabled";
    },

    /**
     * Checks if the paytable was opened on startup.
     *
     * @returns {boolean} Whether the paytable was opened on startup or not.
     */
    paytableOpenedOnStartup: function() {
        return this._paytableOnStartupState === "openedOnStartup";
    },

    /**
     * Sets state of paytable on startup feature.
     *
     * @param {string} state Requested state.
     * @returns {void}
     */
    setPaytableOnStartupState: function(state) {
        var currentState = this._paytableOnStartupState;

        switch (state) {
            case "idle": {
                if (
                    currentState === "disabled" ||
                    currentState === "openedOnStartup"
                ) {
                    this._paytableOnStartupState = "idle";
                }
                break;
            }

            case "setupFinished": {
                if (currentState === "idle") {
                    this._paytableOnStartupState = "setupFinished";
                }
                else if (currentState === "enteredGame") {
                    this._paytableOnStartupState = "readyToOpen";
                }
                break;
            }

            case "enteredGame": {
                if (currentState === "idle") {
                    this._paytableOnStartupState = "enteredGame";
                }
                else if (currentState === "setupFinished") {
                    this._paytableOnStartupState = "readyToOpen";
                }
                break;
            }

            case "openedOnStartup": {
                if (currentState === "readyToOpen") {
                    this._paytableOnStartupState = "openedOnStartup";
                }
                break;
            }

            default: {
                // should display warning in development mode
            }
        }
    },

    /**
     * Opens the paytable when the setup finished and entered game.
     *
     * @returns {void}
     */
    openPaytableOnStartup: function() {
        this.setPaytableOnStartupState("openedOnStartup");

        if (this._paytableOnStartupState === "openedOnStartup") {
            this.open();
        }
    },

    /**
     * Setups listener to open the paytable on entering idle or round restore state.
     *
     * @returns {void}
     */
    showPaytableOnEnteringGame: function() {
        var isRestore = Resources.readData("gameServerInitResponse").restore === true,
            event = isRestore ?
                "notify:stateHandler.enteringRoundRestoreState" :
                "notify:stateHandler.enteringIdleState",
            me = this;

        this.observable.addListener(event, function() {
            me.observable.removeListener(event);

            me.setPaytableOnStartupState("enteredGame");

            me.openPaytableOnStartup();
        });
    },

    /**
      * Get path to template_config.
      *
      * @returns {string} Path to template config.
      */
    getTemplateConfigPath: function() {
        var urlParameters = Resources.readData("queryData"),
            staticServer = urlParameters ? urlParameters.staticServer : null,
            currUrl = window.location.href;

        if (!staticServer) {
            staticServer = currUrl.substring(0, currUrl.lastIndexOf("/games/"));
        }
        staticServer = Sys.utils.removeTrailingSlash(staticServer);

        if (urlParameters && urlParameters.gameLocation) {
            return (
                staticServer +
                    "/" +
                    Sys.utils.removeTrailingSlash(urlParameters.gameLocation) +
                    "/rules/templates_config.json"
            );
        }

        return "../rules/templates_config.json";
    },

    /**
     * Fetches Paytable values from template_config.
     *
     * @returns {Promise<void>} Void promise.
     */
    fetchPaytableValues: function() {
        var result,
            gameId = Resources.readData("queryData").gameId,
            gameIdFormatted = gameId.replace("_sw", ""),
            me = this;

        if (!gameId) {
            return;
        }

        fetch(me.getTemplateConfigPath())
            .then(function(resp) {
                return resp.json();
            })
            .then(function(configFile) {
                result = configFile[gameIdFormatted];

                if (result && result.values) {
                    me._paytableValues = result.values;
                }
            })
            .catch(function() {
                console.warn(
                    "error while retrieving template_config information"
                );
            });
    }
};

Services.Paytable = Sys.extend(
    Services.Base,
    Services.Paytable,
    "Services.Paytable"
);
